import {flow, getEnv, types} from "mobx-state-tree";
import {saveAs} from 'file-saver';
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import Notification from "../../../utils/Notification";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import {DimensionsMultiSelectState} from "../../elements/dimensions/multi-select/dimensionsMultiSelectState";
import lodash from "lodash";
import {MediaFile} from "../../../models/mediaFiles/MediaFileModel";
import {CodeTypeSelectState} from "../../elements/codeTypes/CodeTypeSelectState";
import {
  getFileFormat,
  supportedNonImageFileTypes,
  supportedImageFileTypes,
  getFileGroup,
  convertByteToMB, isEpsFormat, resolveDownloadFilename
} from "../../../utils/common";
import * as pdfjsLib from "pdfjs-dist";
import {fileSizeConstants} from "../../../utils/constants";



export const EditMediaFileState = types.model("EditMediaFileState", {
  mediaFile:types.maybe(MediaFile),
  isLoading: types.optional(types.boolean, false),
  isMadeDefault:types.optional(types.boolean, false),
  groupSelectStore: types.maybe(types.reference(GroupSelectState)),
  tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
  isExclusivelyGroupOwned: types.optional(types.boolean, true),
  dimensionsMultiSelectStore: types.maybeNull(types.late(() => types.reference(DimensionsMultiSelectState))),
  dimensionAll: types.optional(types.boolean, true),
  isDimensionSelectVisible: types.optional(types.boolean, false),
  fileIsCorrectSize: types.optional(types.boolean, true),
  fileIsCorrectType: types.optional(types.boolean, true),
  fileSize: types.optional(types.number, 0),
  fileFormat: types.optional(types.string, ''),
  fileForS3Upload: types.maybe(types.frozen()),
  base64FileImage: types.optional(types.string, ''),
  convertedEpsToPngFile: types.optional(types.frozen(), {}),
  base64NonBrowserConvertedFile: types.optional(types.string, ''),
  base64NonBrowserConvertedFileForStaging: types.optional(types.string, ''),
  codeTypeSelectStore: types.maybeNull(types.reference(CodeTypeSelectState)),
  pdfLogoPng: types.optional(types.string, ''),

}).volatile((self) => ({
  cancelUploads: types.boolean,
  waitingForMediaFileUpload: types.boolean,
  checkStatusHasReachedLimit: types.boolean,
}))
  .actions(self => ({
    setCancelUploads(value) {
      self.cancelUploads = value
    },
    setWaitingForMediaFileUpload(value){
      self.waitingForMediaFileUpload = value
    },
    setBase64NonBrowserConvertedFile(str) {
      self.base64NonBrowserConvertedFile = str
    },
    setBase64NonBrowserConvertedFileForStaging(str) {
      self.base64NonBrowserConvertedFileForStaging = str
    },
    setIsLoading(bool) {
      self.isLoading = bool
    },
    setPdfLogoPng(file) {
      self.pdfLogoPng = file
    },
    setFileIsCorrectSize(bool){
      self.fileIsCorrectSize = bool
    },
    setFileIsCorrectType(correctType){
      self.fileIsCorrectType = correctType
    },
    setFileSize(size){
      self.fileSize = size
    },
    setCheckStatusHasReachedLimit(value){
      self.checkStatusHasReachedLimit = value
    },
    handleIsDefault(){
      self.isMadeDefault = !self.isMadeDefault
    },
    validateForm() {
      let isValid = true;
      isValid = self.mediaFile.validateMediaFileModel() // validate fields other than name
      return isValid;
    },
    toggleIsExclusivelyGroupOwned(){
      self.isExclusivelyGroupOwned = !self.isExclusivelyGroupOwned
    },
    toggleDimensionAll(){
      self.dimensionAll = !self.dimensionAll
      self.toggleIsDimensionSelectVisible(!self.dimensionAll)
      self.dimensionAll ? self.dimensionsMultiSelectStore.selectAllDimensions() : self.dimensionsMultiSelectStore.reset()
    },
    toggleIsDimensionSelectVisible(visible){
      self.isDimensionSelectVisible = visible
    },
    hydrateDimension(dimensions = []){
      const isDimensionAll = Object.values(dimensions).every(item => lodash.isEmpty(item));
      self.dimensionAll = !isDimensionAll
      self.toggleDimensionAll()

      if(!self.dimensionAll){
        self.dimensionsMultiSelectStore.hydrateDimensions(dimensions)
      }
    },
    hydrateMediaFileForm(mediaFile){
      // self.mediaFile.hydrateMediaFile(mediaFile)
      self.groupSelectStore.selectGroup(mediaFile?.owner?.groupUuid)
      self.dimensionsMultiSelectStore?.getListOnGroup(mediaFile?.owner?.groupUuid)

      self.tagSelectStore.setSelectedTags(mediaFile?.tags?.flatMap((tag)=> tag.uuid))
      self.isExclusivelyGroupOwned = mediaFile?.isExclusivelyGroupOwned;
      self.hydrateDimension(mediaFile?.owner?.dimensions)

    },
    setBase64FileImage(base64) {
      self.base64FileImage = base64

    },
    removeFile() {
      self.base64FileImage = ""
    },
    setFileForS3Upload(file){
      self.fileForS3Upload = file
    },
    async convertEpsFileToPreview(file) {
      const manager = getEnv(self).mediaFileManager;

      try {

        const base64SrcImg = await manager.convertEpsToBase64(file);
        self.setBase64NonBrowserConvertedFileForStaging(base64SrcImg) // Set for preview, keep original EPS for upload
      } catch (error) {
        new Notification()
          .setType("error")
          .setMessage(`Failed to handle EPS file: ${error.message}`)
          .send();
        return;
      }
    },
    async setSelectedImage(file, isLogo, eyesAndModulesStore) {


      const fileFormat = getFileFormat(file);
      self.setBase64FileImage("");

      // Test EPS conversion if it's an EPS file
      if (isEpsFormat(file)) {
        self.setIsLoading(true);
        await self.convertEpsFileToPreview(file)
        self.setIsLoading(false);
      }

      // Ensure the new file's format matches the existing file's format
      if (fileFormat !== self.mediaFile?.fileFormat) {
        new Notification()
          .setType("error")
          .setMessage(`File format mismatch: Expected ${self.mediaFile?.fileFormat}, but got ${fileFormat}`)
          .send();
        return;
      }
      // Restrict file size for QR images
      if (self.mediaFile.usage === "qrImage" && file?.size > fileSizeConstants.MAX_UPLOAD_QR_IMAGE) {
        new Notification()
          .setType("error")
          .setMessage(`File size: ${convertByteToMB(file?.size)} MB is larger than qrImage supports.`)
          .send();
        return;
      }

      const correctSize = file?.size < fileSizeConstants.MAX_UPLOAD_CONTENT_FILE; // 200 MG size limit for uploads
      if (!correctSize) {
        new Notification()
          .setType("error")
          .setMessage(`File size: ${convertByteToMB(file?.size)} MB is larger than 200 MB upload limit.`)
          .send();
        return;
      }

      if (isLogo) {
        self.setPdfLogoPng(file)
      } else {
        self.setPdfLogoPng("")
      }

      // Determine if the file type is supported
      const correctType = supportedImageFileTypes(fileFormat.toLowerCase()) || supportedNonImageFileTypes(fileFormat.toLowerCase());

      if (correctSize && correctType) {
        self.setFileForS3Upload(file);
        self.setFileIsCorrectSize(true)
        self.setFileIsCorrectType(correctType)

        self.mediaFile.updateMediaFile({  "fileFormat":fileFormat})

        self.setFileSize(file?.size)

        let reader = new FileReader() // show image preview before upload
        reader.onload = function (event) {
          if (fileFormat === "svg+xml" || fileFormat === "svg") {
            // Keep full data URI for SVG
            self.setBase64FileImage(reader.result);
          } else {
            // Only keep base64 string for other formats
            self.setBase64FileImage(reader.result.split("base64,")[1])
          }
        }

        if (file) {
          reader.readAsDataURL(file);
        }
      } else {
        if (!correctType) {
          new Notification()
            .setType("error")
            .setMessage(`${fileFormat} format is not supported at this time`)
            .send();
        }
        self.setFileIsCorrectSize(correctSize)
        self.setFileIsCorrectType(correctType)
      }
    },

    downloadFile: flow(function* () {
      const originalUrl = self.mediaFile?.liveLinks?.original;
      if (!originalUrl) return;
      const filename = resolveDownloadFilename(self.mediaFile?.name, originalUrl);
      try {
        const manager = getEnv(self).mediaFileManager;
        const blob = yield manager.downloadOriginalFile(originalUrl);
        saveAs(blob, filename);
      } catch (error) {
        new Notification()
          .setType("error")
          .setMessage(`Failed to download ${filename}`)
          .send();
      }
    }),

    updateLocationAndUploadFile: flow(function* f() {
      self.isLoading = true
      self.setCheckStatusHasReachedLimit(false)
      if( self.fileForS3Upload){
        self.setWaitingForMediaFileUpload(false)
        self.mediaFile.updateMediaFile({status: "PENDING_UPLOAD"})
      }
      const manager = getEnv(self).mediaFileManager
      const resp = yield manager.updateLocationAndUploadFile(self);
      if (resp === "success") {
        new Notification()
          .setType("success")
          .setMessage(`${self.mediaFile?.name} Is Updated`)
          .send();
        self.fileForS3Upload = null
      self.isLoading = false
        return resp
      } else {
        new Notification()
          .setType("error")
          .setMessage(`${resp}`)
          .send();
      }
      self.isLoading = false
    }),
    waitForMediaFileUpload: flow(function* (mediaFileUUID) {
      self.isLoading = true;
      self.cancelUploads = false;
      self.setWaitingForMediaFileUpload(true)
      self.setCheckStatusHasReachedLimit(false)

      const fileReady = yield new Promise((resolve) => {
        let count = 0;
        const interval = setInterval(async () => {
          count++;
          const response = await getEnv(self).mediaFileManager?.checkMediaFileStatus(mediaFileUUID);
          if (self.cancelUploads === true) {
            clearInterval(interval);
            resolve(false);
          }else if (response?.status === "UPLOADED") {
            clearInterval(interval);
            resolve(true); // Resolve as "true" if uploaded
            self.setWaitingForMediaFileUpload(false)

            if (response?.singleUseFile === true) {
              self.updateSingleUseMediaFile(response);
            } else {
              self.mediaFile.updateMediaFile(response);
              //if media file is eps then convert to base64 for preview
              if (isEpsFormat(response?.fileFormat)) {
                const base64SrcImg = await getEnv(self).mediaFileManager.convertEpsToBase64(response?.liveLinks?.original);
                self.setBase64NonBrowserConvertedFile(base64SrcImg); // Set for preview, keep original EPS for upload
              } else {
                self.setBase64NonBrowserConvertedFile("");
              }

            }
          } else if (count > 30) {
            self.setCheckStatusHasReachedLimit(true)
            clearInterval(interval);
            resolve(false); // Resolve as "false" after 100 attempts or cancel
            self.setWaitingForMediaFileUpload(false)
            self.mediaFile.updateMediaFile(response);
            new Notification()
              .setType("warning")
              .setMessage("File is taking longer than expected to upload. Come back later")
              .send(); // TODO: make this localised String
            clearInterval(interval);
            resolve(false);
          } else if (response?.status === "FAILED") {
            self.setWaitingForMediaFileUpload(false)
            self.mediaFile.updateMediaFile(response);
            new Notification()
              .setType("error")
              .setMessage("Failed to upload file")
              .send(); // TODO: make this localised String
            clearInterval(interval);
            resolve(false)
          }
        }, 1000);
      });
      self.isLoading = false;
      return fileReady;
    }),
    removeMediaFile() {
      // Clear the media file reference
      self.mediaFile = undefined;

      // Reset related state
      self.base64FileImage = "";
      self.fileForS3Upload = null;
      self.fileSize = 0;
      self.fileFormat = "";
      self.fileIsCorrectSize = true;
      self.fileIsCorrectType = true;
      self.isMadeDefault = false;
      self.pdfLogoPng = "";

      // Reset volatile state
      self.cancelUploads = false;
      self.waitingForMediaFileUpload = false;
      self.checkStatusHasReachedLimit = false;

      // Reset loading state
      self.isLoading = false;

    }


  })).views((self) => ({
    acceptedFileFormat() {
      return self.mediaFile?.fileFormat?.toUpperCase() || "N/A";
    },
    usageIsQRImage() {
      return self.mediaFile?.usage === "qrImage"
    },
    usageIsContent() {
      return self.mediaFile?.usage === "content"
    },
    shouldRenderUploadNewFileButtonAndContent(){
      return self.mediaFile?.status !== "PENDING_UPLOAD"
    },
    shouldRenderFileThatHasBeenUploaded(){
      return self.mediaFile?.status === "UPLOADED"
    },
    shouldRenderFileFailedToUploadBanner(){
      return self.mediaFile?.status === "FAILED"
    },
    shouldRenderFileIsProcessingBanner(){
      return self.mediaFile?.status === "PENDING_UPLOAD"
    },
    shouldCallOnWaitForMediaFileUpload(){
     if( self?.mediaFile && self?.mediaFile.status !== "UPLOADED" &&
      self?.mediaFile.status !== "FAILED" &&
      self?.waitingForMediaFileUpload === false &&
      self?.checkStatusHasReachedLimit !== true){
      self.waitForMediaFileUpload(self?.mediaFile.uuid)
     }
    },
    renderHowManyCodesThisFileIsUsed() {
      return self.mediaFile?.codeUUIDs?.length
    },
    shouldRenderStagedNewFileToUpload() {
      return self.fileForS3Upload
    },
    shouldRenderStagedPDFToUpload() {
      return self.fileForS3Upload?.type === "application/pdf"
    },
    shouldRenderFileIconForS3Upload() {
      return  (self.mediaFile?.fileFormat || "") !== "" &&
        supportedNonImageFileTypes(self.mediaFile?.fileFormat || "") &&
        self.fileForS3Upload?.type !== "application/pdf"
    },
    shouldRenderBase64NonBrowserConvertedFile(){
      return self.base64NonBrowserConvertedFile !== "" && self.base64NonBrowserConvertedFile !== undefined
    },
    shouldRenderImageForS3Upload() {
      return  self.mediaFile?.fileFormat !== "" && supportedImageFileTypes( self.mediaFile?.fileFormat ?? "")
    },
    imagePreviewSrc(){
      const format = self.mediaFile?.fileFormat || "";
      if( isEpsFormat(format)) {
        return self.base64NonBrowserConvertedFileForStaging
      }
      if (isEpsFormat(format) || format === "svg" || format === "svg+xml") {
        return self.base64FileImage;
      }
      return `data:${format};base64,${self.base64FileImage}`;
    },
    getExperienceOptions() {
      const options =  self.experienceOptions||{} ;
      return options || []
    },
  }))

